import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Building,
  Search, 
  Eye,
  RefreshCw,
  Calendar,
  User,
  FileText,
  DollarSign,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const NASBankDeposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [totalDeposited, setTotalDeposited] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Modals
  const [isViewDepositOpen, setIsViewDepositOpen] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);

  useEffect(() => {
    loadData();
  }, [startDate, endDate]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await axios.get(`${API}/api/nas-invoices/bank-deposits`, { params });
      setDeposits(response.data.deposits || []);
      setTotalDeposited(response.data.total_deposited || 0);
    } catch (error) {
      console.error('Error loading bank deposits:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have access to bank deposit information');
      } else {
        toast.error('Failed to load bank deposits');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const viewDeposit = (deposit) => {
    setSelectedDeposit(deposit);
    setIsViewDepositOpen(true);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
  };

  const filteredDeposits = deposits.filter(dep => 
    dep.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dep.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dep.bank_account?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dep.reference_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group deposits by bank account
  const depositsByBank = filteredDeposits.reduce((acc, dep) => {
    const bank = dep.bank_account || 'Unknown';
    if (!acc[bank]) {
      acc[bank] = { deposits: [], total: 0 };
    }
    acc[bank].deposits.push(dep);
    acc[bank].total += dep.amount_deposited || 0;
    return acc;
  }, {});

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Bank Deposits</h1>
          <p className="text-slate-500">Track all bank deposits from customer payments</p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={isLoading}>
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total Deposited</p>
              <p className="text-4xl font-bold">₱{totalDeposited.toLocaleString()}</p>
              <p className="text-purple-200 text-sm mt-1">{filteredDeposits.length} deposits</p>
            </div>
            <Building size={64} className="text-purple-300" />
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search by invoice, customer, bank..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 items-center">
              <Label className="text-sm text-slate-500 whitespace-nowrap">From:</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="flex gap-2 items-center">
              <Label className="text-sm text-slate-500 whitespace-nowrap">To:</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            {(startDate || endDate || searchTerm) && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Deposits by Bank */}
      {isLoading ? (
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
        </div>
      ) : Object.keys(depositsByBank).length === 0 ? (
        <Card className="bg-white">
          <CardContent className="p-10 text-center">
            <Building className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No bank deposits found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(depositsByBank).map(([bank, data]) => (
            <Card key={bank} className="bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Building size={20} className="text-purple-600" />
                    {bank}
                  </span>
                  <span className="text-purple-600">₱{data.total.toLocaleString()}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.deposits.map(deposit => (
                    <div 
                      key={deposit.id} 
                      className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => viewDeposit(deposit)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <DollarSign size={20} className="text-purple-600" />
                          </div>
                          <div>
                            <p className="font-semibold">₱{deposit.amount_deposited?.toLocaleString()}</p>
                            <p className="text-sm text-slate-500">
                              <span className="font-mono">{deposit.invoice_number}</span>
                              {deposit.customer_name && ` • ${deposit.customer_name}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">
                            {new Date(deposit.deposit_date).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-slate-500">Ref: {deposit.reference_number}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* All Deposits Table View */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg">All Deposits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Invoice</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Bank Account</th>
                  <th className="text-left p-3">Reference #</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Recorded By</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeposits.map(deposit => (
                  <tr key={deposit.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => viewDeposit(deposit)}>
                    <td className="p-3">{new Date(deposit.deposit_date).toLocaleDateString()}</td>
                    <td className="p-3 font-mono text-xs">{deposit.invoice_number}</td>
                    <td className="p-3">{deposit.customer_name}</td>
                    <td className="p-3">{deposit.bank_account}</td>
                    <td className="p-3">{deposit.reference_number}</td>
                    <td className="p-3 text-right font-semibold text-purple-600">₱{deposit.amount_deposited?.toLocaleString()}</td>
                    <td className="p-3 text-slate-500">{deposit.recorded_by}</td>
                  </tr>
                ))}
                {filteredDeposits.length > 0 && (
                  <tr className="border-t bg-purple-50">
                    <td colSpan={5} className="p-3 font-semibold">Total</td>
                    <td className="p-3 text-right font-bold text-purple-700">
                      ₱{filteredDeposits.reduce((sum, d) => sum + (d.amount_deposited || 0), 0).toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* View Deposit Modal */}
      <Dialog open={isViewDepositOpen} onOpenChange={setIsViewDepositOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="text-purple-600" />
              Bank Deposit Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedDeposit && (
            <div className="space-y-4 py-4">
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-purple-600 text-sm">Amount Deposited</p>
                <p className="text-3xl font-bold text-purple-700">₱{selectedDeposit.amount_deposited?.toLocaleString()}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Deposit Date</p>
                  <p className="font-medium">{new Date(selectedDeposit.deposit_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-slate-500">Reference #</p>
                  <p className="font-medium">{selectedDeposit.reference_number}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Bank Account</p>
                  <p className="font-medium">{selectedDeposit.bank_account}</p>
                </div>
                <div>
                  <p className="text-slate-500">Invoice</p>
                  <p className="font-mono text-xs">{selectedDeposit.invoice_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Order</p>
                  <p className="font-mono text-xs">{selectedDeposit.delivery_code}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Customer</p>
                  <p className="font-medium">{selectedDeposit.customer_name}</p>
                </div>
                {selectedDeposit.notes && (
                  <div className="col-span-2">
                    <p className="text-slate-500">Notes</p>
                    <p>{selectedDeposit.notes}</p>
                  </div>
                )}
                <div className="col-span-2 pt-2 border-t">
                  <p className="text-slate-500 text-xs">
                    Recorded by {selectedDeposit.recorded_by} on {new Date(selectedDeposit.recorded_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDepositOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASBankDeposits;
